import React, { useCallback, useState } from 'react';
import { Box, Paper, Typography, CircularProgress, Link } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import axios, { AxiosError } from 'axios';
import { API_ENDPOINTS } from '../config';
import { Download as DownloadIcon } from '@mui/icons-material';

interface FileUploadProps {
  onUploadComplete: (data: any[]) => void;
}

interface ErrorResponse {
  message: string;
}

const FileUpload: React.FC<FileUploadProps> = ({ onUploadComplete }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    // Check if file is Excel
    if (!file.name.match(/\.(xlsx|xls)$/)) {
      setError('Vui lòng tải lên file Excel (.xlsx hoặc .xls)');
      return;
    }

    setUploading(true);
    setError(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(API_ENDPOINTS.UPLOAD.IMPORT, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      if (response.data && response.data.addresses && Array.isArray(response.data.addresses)) {
        onUploadComplete(response.data.addresses);
        setError(null); // Ensure error is cleared on success
      } else {
        setError('Dữ liệu trả về không đúng định dạng');
      }
    } catch (err) {
      const error = err as AxiosError<ErrorResponse>;
      const errorMessage = error.response?.data?.message || 'Lỗi khi tải lên file';
      setError(errorMessage);
      console.error('Upload error:', error); // Log the full error for debugging
    } finally {
      setUploading(false);
    }
  }, [onUploadComplete]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    multiple: false
  });

  return (
    <Box sx={{ mb: 4 }}>
      <Paper
        sx={{
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" gutterBottom>
            Nhập danh sách địa chỉ từ file Excel
          </Typography>
          <Link
            href="/import_template.xlsx"
            download
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: 'primary.main',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
          >
            <DownloadIcon fontSize="small" />
            Tải template mẫu
          </Link>
        </Box>
        <Paper
          {...getRootProps()}
          sx={{
            p: 3,
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
            border: '2px dashed',
            borderColor: isDragActive ? 'primary.main' : 'divider',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <input {...getInputProps()} />
          {uploading ? (
            <CircularProgress />
          ) : (
            <Typography>
              {isDragActive
                ? 'Thả file Excel vào đây'
                : 'Kéo và thả file Excel vào đây, hoặc click để chọn file'}
            </Typography>
          )}
          {error && (
            <Typography color="error" sx={{ mt: 1 }}>
              {error}
            </Typography>
          )}
        </Paper>
      </Paper>
    </Box>
  );
};

export default FileUpload; 